import WidgetResize from '@ckeditor/ckeditor5-widget/src/widgetresize';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';

const annotationOuterElement = 'span';

// get structure of annotation HTML element
const makeAnnotationElementStructure = ({
  itemId = null,
  width = '50%',
  itemFieldType = null,
  style = '',
  isMultiple,
} = {}) => ({
  outer: {
    element: annotationOuterElement,
    attributes: {
      class: 'annotation',
      'data-itemid': itemId,
      'data-itemfieldtype': itemFieldType,
      'data-multiple': isMultiple,
      style,
    },
  },
  inner: {
    element: 'span',
    attributes: { class: 'annotation-text' },
  },
});

// generate text to be displayed within annotation
const makeAnnotationText = (input) => {
  if (input.itemfieldtype === 'SIGNATUREINPUT') {
    return 'Signature';
  }
  return `{{ ${input.itemdisplaylabel} }}`;
};

// extract annotation data from view element
const getAnnotationDataFromView = (viewElement, domConverter) => {
  const { inner } = makeAnnotationElementStructure();
  const children = Array.from(viewElement.getChildren());
  // get text from child span
  const innerElement = children.find(
    (element) => element.is('element', inner.element) && element.hasClass(inner.attributes.class),
  );
  const itemId = viewElement.getAttribute('data-itemid');
  const itemFieldType = viewElement.getAttribute('data-itemfieldtype');
  const isMultiple = viewElement.getAttribute('data-multiple');
  const width = viewElement.getStyle('width') || '50%';
  const style = viewElement.getAttribute('style') || '';

  let annotaionText;
  if (innerElement) {
    const domElement = domConverter.viewToDom(innerElement);
    annotaionText = domElement?.innerHTML || '';
  } else {
    annotaionText = '';
  }

  return {
    displayLabel: annotaionText,
    itemId,
    itemFieldType,
    isMultiple,
    width,
    style,
  };
};

// Helper method for both downcast converters.
function createAnnotationView(modelItem, viewWriter) {
  const displayLabel = modelItem.getAttribute('displayLabel');
  const itemId = modelItem.getAttribute('itemId');
  const itemFieldType = modelItem.getAttribute('itemFieldType');
  const isMultiple = modelItem.getAttribute('isMultiple');
  const width = modelItem.getAttribute('width');
  const style = modelItem.getAttribute('style') || '';

  const options = { itemId, width, itemFieldType, isMultiple, style };
  const { outer } = makeAnnotationElementStructure(options);

  const annotation = viewWriter.createContainerElement(outer.element, outer.attributes);

  let viewFragment;
  const specialAnnotations = ['imageinput', 'signatureinput'];
  const initialLabelRegex = /(\{([^)]+)\}|Signature)/;

  // Insert content into annotation inner span.
  // Using createRawElement will render HTML strings.
  if (
    specialAnnotations.includes(itemFieldType?.toLowerCase()) &&
    !initialLabelRegex.test(displayLabel)
  ) {
    viewFragment = viewWriter.createRawElement(
      'img',
      { class: 'annotation-text' },
      (domElement) => {
        domElement.src = displayLabel;
      },
    );
  } else {
    viewFragment = viewWriter.createRawElement(
      'span',
      { class: 'annotation-text' },
      (domElement) => {
        domElement.innerHTML = displayLabel;
      },
    );
  }

  viewWriter.insert(viewWriter.createPositionAt(annotation, 0), viewFragment);

  return annotation;
}

// make new annotation widget
const makeAnnotationWidget = (editor, modelItem, viewWriter, resizable = false) => {
  const widget = toWidget(createAnnotationView(modelItem, viewWriter), viewWriter);

  if (resizable) {
    const resizer = new WidgetResize(editor);
    resizer.init();
    resizer.attachTo({
      editor,
      modelElement: modelItem,
      viewElement: widget,
      getHandleHost: (wrapper) => wrapper,
      getResizeHost: (wrapper) => wrapper,
      onCommit: (newValue) => {
        const model = editor.model;
        model.change((writer) => {
          writer.setAttribute('width', newValue, modelItem);
        });
      },
    });
  }

  return widget;
};

export {
  makeAnnotationElementStructure,
  getAnnotationDataFromView,
  createAnnotationView,
  makeAnnotationText,
  makeAnnotationWidget,
  annotationOuterElement,
};
